/**
 * seedData.ts
 *
 * Populates localStorage with realistic demo data for QA and development.
 * Triggered by visiting /?seed-data (or ?seed-data=reset to wipe first).
 *
 * Writes to: kf-projects, kf-tasks, kf-task-sessions, kf-session-log
 * Does NOT touch: kf-settings
 */

import type { Project, Task, TaskSession, SessionLogEntry, SessionType } from '../types';

// ---------------------------------------------------------------------------
// Storage keys (kept in sync with LocalProvider.ts and sessionLog.ts)
// ---------------------------------------------------------------------------
const KF_PROJECTS = 'kf-projects';
const KF_TASKS = 'kf-tasks';
const KF_TASK_SESSIONS = 'kf-task-sessions';
const KF_SESSION_LOG = 'kf-session-log';

// ---------------------------------------------------------------------------
// Deterministic pseudo-random number generator (mulberry32)
// A fixed seed makes every QA run produce identical data.
// ---------------------------------------------------------------------------
function makePrng(seed: number): () => number {
  let s = seed;
  return function next(): number {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = makePrng(0xdeadbeef);

function randInt(min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(rand() * arr.length)];
}

// ---------------------------------------------------------------------------
// Seed content
// ---------------------------------------------------------------------------

const PROJECT_NAMES = [
  'Website Redesign',
  'Q3 Planning',
  'Bug Fixes',
  'Mobile App',
  'Content Strategy',
  'API Integration',
  'UX Research',
  'Performance Optimisation',
];

const TASK_TEMPLATES: Record<string, string[]> = {
  'Website Redesign': [
    'Wireframe homepage layout',
    'Design hero section',
    'Review competitor sites',
    'Update colour palette',
    'Implement responsive grid',
    'Write copy for About page',
    'Optimise images for web',
    'Set up A/B testing',
    'User testing session',
    'Final QA review',
  ],
  'Q3 Planning': [
    'Define OKRs for the quarter',
    'Roadmap review with team',
    'Budget allocation spreadsheet',
    'Stakeholder kickoff deck',
    'Risk assessment',
    'Sprint planning session',
    'Write project charter',
    'Set up tracking dashboard',
    'Capacity planning',
  ],
  'Bug Fixes': [
    'Fix login redirect loop',
    'Resolve timer drift on mobile',
    'Patch XSS in search input',
    'Debug session storage race condition',
    'Fix CSS overflow on Safari',
    'Correct timezone offset in reports',
    'Resolve 404 on deep link',
    'Fix dark mode flicker on load',
  ],
  'Mobile App': [
    'Set up React Native project',
    'Implement push notifications',
    'Design onboarding flow',
    'Integrate biometric auth',
    'Offline sync strategy',
    'Write E2E tests',
    'App Store listing copy',
    'Beta TestFlight release',
    'Crash analytics integration',
  ],
  'Content Strategy': [
    'Audit existing blog posts',
    'Keyword research',
    'Editorial calendar Q3',
    'Draft "Getting Started" guide',
    'Record tutorial video scripts',
    'Social media content plan',
    'Newsletter template design',
    'SEO meta tags review',
  ],
  'API Integration': [
    'OAuth 2.0 flow design',
    'Document REST endpoints',
    'Build webhook handler',
    'Rate limiting middleware',
    'Write Postman collection',
    'Error handling review',
    'Integration tests suite',
    'API versioning strategy',
  ],
  'UX Research': [
    'Recruit usability test participants',
    'Create interview script',
    'Conduct user interviews',
    'Affinity mapping',
    'Synthesise findings report',
    'Persona update',
    'Journey map revision',
    'Present insights to stakeholders',
  ],
  'Performance Optimisation': [
    'Lighthouse audit baseline',
    'Reduce main-thread blocking',
    'Lazy-load route components',
    'Implement service worker caching',
    'Analyse bundle size',
    'Tree-shake unused dependencies',
    'Image format migration to WebP',
    'Database query profiling',
    'CDN configuration review',
  ],
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function toDateString(epochMs: number): string {
  return new Date(epochMs).toISOString().split('T')[0];
}

function startOfDayMs(epochMs: number): number {
  const d = new Date(epochMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ---------------------------------------------------------------------------
// Main seed function
// ---------------------------------------------------------------------------

export function seedDemoData(reset = false): void {
  if (reset) {
    localStorage.removeItem(KF_PROJECTS);
    localStorage.removeItem(KF_TASKS);
    localStorage.removeItem(KF_TASK_SESSIONS);
    localStorage.removeItem(KF_SESSION_LOG);
  }

  const now = Date.now();

  // -------------------------------------------------------------------------
  // 1. Projects — 8 deterministic projects
  // -------------------------------------------------------------------------
  const projects: Project[] = PROJECT_NAMES.map((name, i) => ({
    id: crypto.randomUUID(),
    name,
    collapsed: false,
    sortOrder: i,
    createdAt: now - (30 - i) * 24 * 60 * 60 * 1000,
  }));

  // -------------------------------------------------------------------------
  // 2. Tasks — 5–8 per project, last 3 tasks across all projects are backlog
  //    (no sessions), simulating work not yet started.
  // -------------------------------------------------------------------------
  const BACKLOG_TASK_COUNT = 3;

  const tasks: Task[] = [];

  projects.forEach((project) => {
    const templateList = TASK_TEMPLATES[project.name] ?? [];
    const taskCount = Math.min(randInt(5, 8), templateList.length);

    for (let ti = 0; ti < taskCount; ti++) {
      tasks.push({
        id: crypto.randomUUID(),
        text: templateList[ti] ?? `Task ${ti + 1}`,
        projectId: project.id,
        completed: false,
        sortOrder: ti,
        createdAt: project.createdAt + ti * 60 * 1000,
        totalTimeSpent: 0, // populated after sessions are generated
        sessions: [],      // legacy field — kept for backward compatibility
      });
    }
  });

  // The last N tasks receive no sessions (backlog)
  const backlogTaskIds = new Set(
    tasks.slice(-BACKLOG_TASK_COUNT).map(t => t.id)
  );

  // Tasks eligible to receive sessions
  const assignableTasks = tasks.filter(t => !backlogTaskIds.has(t.id));

  // -------------------------------------------------------------------------
  // 3. Schedule work sessions over the past 28 days
  // -------------------------------------------------------------------------
  const SPAN_DAYS = 28;
  const TARGET_WORK_SESSIONS = 125;
  const TASK_LINK_RATE = 0.70;

  // Build per-day work session counts; ~20% chance of a zero-session day
  const daySessionCounts: number[] = [];
  let totalScheduled = 0;

  for (let d = 0; d < SPAN_DAYS; d++) {
    if (rand() < 0.20) {
      daySessionCounts.push(0);
    } else {
      const count = randInt(3, 7);
      daySessionCounts.push(count);
      totalScheduled += count;
    }
  }

  // Scale to hit TARGET_WORK_SESSIONS
  const scaleFactor = TARGET_WORK_SESSIONS / Math.max(totalScheduled, 1);
  const scaledCounts = daySessionCounts.map(c =>
    c === 0 ? 0 : Math.max(1, Math.round(c * scaleFactor))
  );

  // -------------------------------------------------------------------------
  // 4. Generate SessionLogEntry and TaskSession records
  // -------------------------------------------------------------------------
  const sessionLog: SessionLogEntry[] = [];
  const taskSessionAccum: Record<string, TaskSession[]> = {};
  const allTaskSessions: TaskSession[] = [];

  const PLANNED_WORK_SECS = 25 * 60;
  const SHORT_BREAK_SECS = 5 * 60;
  const LONG_BREAK_SECS = 15 * 60;

  for (let dayIdx = 0; dayIdx < SPAN_DAYS; dayIdx++) {
    const workCount = scaledCounts[dayIdx];
    if (workCount === 0) continue;

    // dayIdx 0 = SPAN_DAYS ago, dayIdx SPAN_DAYS-1 = yesterday
    const dayStart = startOfDayMs(now - (SPAN_DAYS - dayIdx) * 24 * 60 * 60 * 1000);

    // Sessions begin at 09:00 on that day
    let cursor = dayStart + 9 * 60 * 60 * 1000;

    for (let w = 0; w < workCount; w++) {
      const workActualSecs = randInt(10 * 60, 25 * 60);
      const workStart = cursor;
      const workEnd = workStart + workActualSecs * 1000;

      // Never generate future-dated sessions
      if (workEnd >= now) break;

      const workSessionId = crypto.randomUUID();
      const workDate = toDateString(workStart);

      const linkToTask = rand() < TASK_LINK_RATE;
      const linkedTask =
        linkToTask && assignableTasks.length > 0 ? pick(assignableTasks) : null;

      sessionLog.push({
        sessionId: workSessionId,
        sessionType: 'pomodoro',
        startTime: workStart,
        endTime: workEnd,
        plannedDuration: PLANNED_WORK_SECS,
        actualDuration: workActualSecs,
        completionStatus: 'completed',
        date: workDate,
      });

      if (linkedTask) {
        const ts: TaskSession = {
          sessionId: crypto.randomUUID(),
          startTime: workStart,
          endTime: workEnd,
          duration: workActualSecs,
          date: workDate,
        };
        if (!taskSessionAccum[linkedTask.id]) {
          taskSessionAccum[linkedTask.id] = [];
        }
        taskSessionAccum[linkedTask.id].push(ts);
        allTaskSessions.push(ts);
      }

      // 2-minute gap between sessions
      cursor = workEnd + 2 * 60 * 1000;

      // Break after every work session; long break every 4th
      const isLongBreak = (w + 1) % 4 === 0;
      const breakType: SessionType = isLongBreak ? 'longBreak' : 'shortBreak';
      const breakPlanned = isLongBreak ? LONG_BREAK_SECS : SHORT_BREAK_SECS;
      const breakActual = isLongBreak
        ? randInt(10 * 60, 15 * 60)
        : randInt(3 * 60, 5 * 60);
      const breakStart = cursor;
      const breakEnd = breakStart + breakActual * 1000;

      if (breakEnd < now) {
        sessionLog.push({
          sessionId: crypto.randomUUID(),
          sessionType: breakType,
          startTime: breakStart,
          endTime: breakEnd,
          plannedDuration: breakPlanned,
          actualDuration: breakActual,
          completionStatus: 'completed',
          date: toDateString(breakStart),
        });
        cursor = breakEnd + 1 * 60 * 1000;
      }
    }
  }

  // -------------------------------------------------------------------------
  // 5. Back-fill totalTimeSpent on each Task
  // -------------------------------------------------------------------------
  tasks.forEach(task => {
    const sessions = taskSessionAccum[task.id] ?? [];
    task.totalTimeSpent = sessions.reduce((acc, s) => acc + s.duration, 0);
    task.sessions = []; // keep legacy field empty to avoid duplication
  });

  // -------------------------------------------------------------------------
  // 6. Persist to localStorage
  // -------------------------------------------------------------------------
  localStorage.setItem(KF_PROJECTS, JSON.stringify(projects));
  localStorage.setItem(KF_TASKS, JSON.stringify(tasks));
  localStorage.setItem(KF_TASK_SESSIONS, JSON.stringify(allTaskSessions));
  localStorage.setItem(KF_SESSION_LOG, JSON.stringify(sessionLog));

  const workCount = sessionLog.filter(e => e.sessionType === 'pomodoro').length;
  // eslint-disable-next-line no-console
  console.info(
    `Demo data loaded — ${workCount} work sessions across ${projects.length} projects` +
    ` (${tasks.length} tasks, ${allTaskSessions.length} task-linked sessions)`
  );
}
